import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './HomePage.css';

function HomePage({ config }) {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productCount, setProductCount] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/api/homepage');
        const data = await response.json();

        let homepageCategories = (data.categories || []).slice(0, 8);
        let homepageProducts = data.featured || [];

        // Active domain catalogs may not have a prebuilt homepage cache yet.
        // Fall back to their live Thomas categories and representative products.
        if (homepageCategories.length === 0 || homepageProducts.length === 0) {
          const categoryResponse = await fetch('/api/categories');
          const liveCategories = await categoryResponse.json();

          if (Array.isArray(liveCategories) && liveCategories.length > 0) {
            homepageCategories = liveCategories.slice(0, 8);

            const productResponses = await Promise.all(
              homepageCategories.slice(0, 4).map((category) =>
                fetch(`/api/categories/${category.id}`).then((result) => result.json())
              )
            );

            homepageProducts = productResponses
              .flatMap((result) => result.products || [])
              .filter((product, index, products) =>
                products.findIndex((candidate) => candidate.id === product.id) === index
              )
              .slice(0, 8);
          }
        }

        setProductCount(data.stats?.products || homepageProducts.length);
        setFeaturedProducts(homepageProducts);
        setCategories(homepageCategories);
        setLoading(false);

        // Reinitialize AOS animations after React content loads
        if (typeof window !== 'undefined' && window.initAOS) {
          setTimeout(() => window.initAOS(), 100);
        }
      } catch (error) {
        console.error('Error fetching homepage data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Check if this is a clothing/fashion site
  const tagline = config.tagline?.toLowerCase() || '';
  const isClothingSite = tagline.includes('kids') || tagline.includes('clothing') || tagline.includes('fashion');
  const isBeautySite = tagline.includes('cosmetic') || tagline.includes('beauty') || tagline.includes('personal care');

  // Generate description based on site type
  const getDescription = () => {
    if (config.description) {
      return config.description;
    }
    if (isClothingSite) {
      return `Discover premium ${config.tagline?.toLowerCase() || 'products'} from top brands. Shop the latest styles with fast shipping and easy returns.`;
    }
    if (isBeautySite) {
      return 'Explore cosmetics, beauty-care products, bath and body formulas, applicators, ingredients, fragrances, and cosmetic packaging from established suppliers.';
    }
    return `Your trusted partner for ${config.tagline?.toLowerCase() || 'industrial supplies'}. Connecting buyers with verified suppliers across North America.`;
  };

  const seoDescription = `${config.name} - ${getDescription()}`;

  return (
    <div className="homepage">
      <SEO
        title={`${config.name} - ${config.tagline}`}
        description={seoDescription}
        type="website"
        siteName={config.name}
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container position-relative">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center" data-aos="fade-up">
              <span className="badge bg-light text-primary mb-3">{isClothingSite ? 'Shop the Latest Trends' : isBeautySite ? 'Cosmetics & Personal Care Marketplace' : 'Specialized Product Marketplace'}</span>
              <h1 className="hero-title">{config.name}</h1>
              <p className="hero-subtitle">{config.tagline}</p>
              <p className="hero-description">
                {getDescription()}
              </p>

              {productCount !== null && productCount > 0 && (
                <div className="hero-stats" data-aos="fade-up" data-aos-delay="100">
                  <div className="stat-box">
                    <span className="stat-number">{productCount.toLocaleString()}</span>
                    <span className="stat-label">{isClothingSite ? 'Styles' : isBeautySite ? 'Cosmetics Products' : 'Products'}</span>
                  </div>
                </div>
              )}

              <div className="hero-buttons" data-aos="fade-up" data-aos-delay="200">
                <Link to="/categories" className="btn btn-light btn-lg">
                  <i className="bi bi-grid me-2"></i>Browse Products
                </Link>
                <Link to="/contact" className="btn btn-outline-light btn-lg">
                  <i className="bi bi-chat-dots me-2"></i>Request Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path fill="#ffffff" d="M0,50 C150,100 350,0 600,50 C850,100 1050,0 1200,50 C1350,100 1440,50 1440,50 L1440,100 L0,100 Z"></path>
          </svg>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="trust-section py-5">
        <div className="container">
          <div className="row g-4">
            {[
              { icon: 'bi-search', title: 'Focused Catalog', desc: `Products selected for ${config.tagline.toLowerCase()}` },
              { icon: 'bi-building', title: 'Supplier Information', desc: 'Review products and supplier capabilities in one place' },
              { icon: 'bi-grid', title: 'Category Browsing', desc: 'Explore relevant products organized by category' },
              { icon: 'bi-chat-dots', title: 'Request a Quote', desc: 'Contact suppliers for specifications, availability, and pricing' }
            ].map((item, idx) => (
              <div key={idx} className="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay={idx * 100}>
                <div className="trust-card">
                  <div className="trust-icon">
                    <i className={`bi ${item.icon}`}></i>
                  </div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5" data-aos="fade-up">
            <span className="badge bg-primary mb-2">Browse Our Catalog</span>
            <h2 className="section-title">Shop by Category</h2>
            <p className="section-subtitle">
              {config.categoryIntro || (isBeautySite ? 'Explore cosmetics, personal-care formulations, applicators, ingredients, and packaging' : 'Explore our product catalog')}
            </p>
          </div>

          {categories.length > 0 ? (
            <>
              <div className="row g-4">
                {categories.map((category, idx) => (
                  <div key={category.id} className="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay={idx * 50}>
                    <Link to={`/category/${category.id}`} className="category-card">
                      <div className="category-image">
                        {category.image ? (
                          <img src={category.image} alt={category.name} />
                        ) : (
                          <div className="category-placeholder">
                            <i className="bi bi-box-seam"></i>
                          </div>
                        )}
                      </div>
                      <div className="category-content">
                        <h3>{category.name}</h3>
                        {category.description && <p>{category.description}</p>}
                        <span className="category-link">
                          View Products <i className="bi bi-arrow-right"></i>
                        </span>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
              <div className="text-center mt-5" data-aos="fade-up">
                <Link to="/categories" className="btn btn-primary btn-lg">
                  View All Categories <i className="bi bi-arrow-right ms-2"></i>
                </Link>
              </div>
            </>
          ) : (
            <p className="text-center">Loading categories...</p>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="features-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5 mb-4 mb-lg-0" data-aos="fade-right">
              <span className="badge bg-primary mb-2">Why Choose Us</span>
              <h2 className="section-title">Why Choose {config.name}?</h2>
              <p className="text-muted mb-4">
                {config.description
                  ? `Use ${config.name} to explore a focused catalog of ${config.tagline.toLowerCase()}, compare available products, and contact suppliers for specifications and quotes.`
                  : isBeautySite
                  ? 'Find established cosmetic manufacturers, formulators, ingredient suppliers, applicator makers, and packaging specialists in one focused catalog.'
                  : "We're committed to providing the best industrial supply experience with competitive pricing, reliable shipping, and expert support."}
              </p>
              <Link to="/about" className="btn btn-outline-primary">
                Learn More <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
            <div className="col-lg-7">
              <div className="row g-4">
                {[
                  ...(isBeautySite ? [
                    { icon: 'bi-stars', title: 'Beauty Categories', desc: 'Bath and body, foundations, fragrances, applicators, ingredients, and packaging' },
                    { icon: 'bi-search', title: 'Focused Selection', desc: 'Cosmetics-related products and services selected from the Thomas catalog' },
                    { icon: 'bi-building', title: 'Established Suppliers', desc: 'Manufacturers, formulators, component makers, and packaging specialists' },
                    { icon: 'bi-chat-dots', title: 'Supplier Inquiries', desc: 'Request product details, manufacturing capabilities, and quotes' }
                  ] : [
                    { icon: 'bi-search', title: 'Relevant Products', desc: `Catalog results filtered for ${config.tagline.toLowerCase()}` },
                    { icon: 'bi-grid', title: 'Clear Categories', desc: 'Browse products through focused, relevant categories' },
                    { icon: 'bi-building', title: 'Supplier Capabilities', desc: 'Review available products and manufacturing services' },
                    { icon: 'bi-chat-dots', title: 'Direct Inquiries', desc: 'Request product details, specifications, and quotes' }
                  ])
                ].map((feature, idx) => (
                  <div key={idx} className="col-md-6" data-aos="fade-up" data-aos-delay={idx * 100}>
                    <div className="feature-card">
                      <div className="feature-icon">
                        <i className={`bi ${feature.icon}`}></i>
                      </div>
                      <h4>{feature.title}</h4>
                      <p>{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="products-section py-5 bg-light">
          <div className="container">
            <div className="text-center mb-5" data-aos="fade-up">
              <span className="badge bg-primary mb-2">Top Picks</span>
              <h2 className="section-title">Featured Products</h2>
              <p className="section-subtitle">{config.featuredIntro || (isBeautySite ? 'Explore selected cosmetics products and supplier capabilities' : 'Discover selected products from the catalog')}</p>
            </div>

            <div className="row g-4">
              {featuredProducts.map((product, idx) => (
                <div key={product.id} className="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay={idx * 50}>
                  <Link to={`/products/${product.slug || product.id}`} className="product-card">
                    <div className="product-image">
                      {product.images && product.images.length > 0 ? (
                        <img src={product.images[0].src} alt={product.name} />
                      ) : (
                        <div className="no-image">
                          <i className="bi bi-image"></i>
                        </div>
                      )}
                      {parseFloat(product.sale_price) > 0 && (
                        <span className="sale-badge">Sale</span>
                      )}
                    </div>
                    <div className="product-content">
                      <h3>{product.name}</h3>
                      <div className="product-price">
                        {parseFloat(product.sale_price) > 0 ? (
                          <>
                            <span className="current-price">${product.sale_price}</span>
                            <span className="original-price">${product.regular_price}</span>
                          </>
                        ) : parseFloat(product.regular_price) > 0 ? (
                          <span className="current-price">${product.regular_price}</span>
                        ) : (
                          <span className="current-price">Request Quote</span>
                        )}
                      </div>
                      <span className="view-product">
                        View Details <i className="bi bi-arrow-right"></i>
                      </span>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section py-5">
        <div className="container">
          <div className="cta-box" data-aos="fade-up">
            <div className="row align-items-center">
              <div className="col-lg-8">
                <h2>Ready to Get Started?</h2>
                <p>Contact us today for competitive pricing and expert assistance.</p>
              </div>
              <div className="col-lg-4 text-lg-end mt-3 mt-lg-0">
                <Link to="/contact" className="btn btn-light btn-lg">
                  Contact Us <i className="bi bi-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
